/**
 * TopicBar — The soul of the canvas workspace.
 *
 * Always visible. A warm glass strip that anchors the entire app.
 * When empty: a quiet presence with search + create.
 * When populated: an Arc-browser-like ribbon of colored topic tokens.
 *
 * Design: "Warm Atelier" — linen surfaces, coral soul, precision without coldness.
 */

import { useState, useRef, useEffect, useCallback, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  Plus,
  X,
  Search,
  Loader2,
  Check,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import { useTopicBar } from '../../contexts/TopicBarContext';
import { topicsApi, type Topic } from '../../api/topics';
import { useChatSession } from '../../contexts/ChatSessionContext';
import { useToast } from '../../contexts/ToastContext';
import { getColorHex, getColorTint } from './topicColors';

// Topics are type-agnostic — they evolve as agents and humans bring things in

// ── TopicBar ───────────────────────────────────────────────────────────────────

export function TopicBar({ onToggleSidebar }: { onToggleSidebar?: () => void } = {}) {
  const {
    openTopics,
    focusedTopicId,
    isMinimized,
    focusTopic,
    removeTopic,
    createTopic,
    newlyCreatedId,
  } = useTopicBar();

  const { toggle: toggleChat } = useChatSession();
  const toast = useToast();
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const newMenuRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  // Track IDs present at mount — only animate tabs that arrive after initial render
  const initialTabIdsRef = useRef<Set<string> | null>(null);
  if (initialTabIdsRef.current === null && openTopics.length > 0) {
    initialTabIdsRef.current = new Set(openTopics.map(t => t.id));
  }

  // Close new-topic menu on outside click
  useEffect(() => {
    if (!showNewMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (newMenuRef.current && !newMenuRef.current.contains(e.target as Node)) {
        setShowNewMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showNewMenu]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 't') {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
        e.preventDefault();
        setShowNewMenu(true);
      }
      if (e.key === 'Escape' && focusedTopicId) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
        focusTopic(null);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'w' && focusedTopicId) {
        e.preventDefault();
        removeTopic(focusedTopicId);
      }
      if ((e.metaKey || e.ctrlKey) && e.key >= '1' && e.key <= '6') {
        const idx = parseInt(e.key, 10) - 1;
        if (idx < openTopics.length) {
          e.preventDefault();
          focusTopic(openTopics[idx].id);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [focusedTopicId, focusTopic, removeTopic, openTopics]);

  const handleCreateTopic = useCallback(async () => {
    setIsCreating(true);
    setShowNewMenu(false);
    try {
      await createTopic({ title: 'New Topic' });
    } catch {
      toast.error('Failed to create topic');
    }
    setIsCreating(false);
  }, [createTopic, toast]);

  const hasTopics = openTopics.length > 0;

  return (
    <div
      className="relative flex-shrink-0 flex items-center px-2 gap-1"
      style={{
        height: hasTopics ? 42 : 36,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(250,250,249,0.88) 100%)',
        backdropFilter: 'blur(12px) saturate(1.4)',
        WebkitBackdropFilter: 'blur(12px) saturate(1.4)',
        borderBottom: '1px solid rgba(231,229,228,0.5)',
        transition: 'height 280ms cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}
    >
      {/* ── Martha logo (compact) — clickable in immersive mode to toggle sidebar */}
      <button
        className="flex-shrink-0 select-none flex items-center gap-1"
        onClick={onToggleSidebar}
        disabled={!onToggleSidebar}
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: 'rgb(var(--c-coral-500, 217 127 111))',
          letterSpacing: '-0.03em',
          marginRight: 2,
          cursor: onToggleSidebar ? 'pointer' : 'default',
          borderRadius: 4,
          padding: '2px 4px',
          transition: 'background 150ms',
          border: 'none',
          background: 'transparent',
        }}
        onMouseEnter={(e) => {
          if (onToggleSidebar) (e.currentTarget as HTMLElement).style.background = 'rgb(var(--c-coral-500, 217 127 111) / 0.08)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = 'transparent';
        }}
        title={onToggleSidebar ? 'Toggle sidebar (⌘B)' : undefined}
      >
        m
      </button>

      {/* ── New topic button ──────────────────────────────────────────── */}
      <div ref={newMenuRef} className="relative flex-shrink-0">
        <button
          onClick={() => handleCreateTopic()}
          disabled={isCreating}
          className="
            flex items-center justify-center rounded-md
            text-neutral-400 hover:text-neutral-600
            disabled:opacity-40
            transition-all duration-200
          "
          style={{
            width: 26,
            height: 26,
            background: showNewMenu ? 'rgba(231,229,228,0.6)' : 'transparent',
          }}
          title="New topic (⌘T)"
        >
          {isCreating ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Plus
              size={14}
              style={{
                transform: showNewMenu ? 'rotate(45deg)' : 'rotate(0)',
                transition: 'transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
              }}
            />
          )}
        </button>

        {/* Click creates immediately — no type picker needed */}
      </div>

      {/* Separator + count badge */}
      {hasTopics ? (
        <div className="flex items-center gap-1 flex-shrink-0 mx-0.5">
          <div className="w-px" style={{ height: 16, background: 'rgba(231,229,228,0.5)' }} />
          <span
            className="flex-shrink-0"
            style={{
              fontSize: 9,
              fontWeight: 600,
              color: '#a8a29e',
              minWidth: 16,
              height: 16,
              lineHeight: '16px',
              textAlign: 'center',
              borderRadius: 4,
              background: 'rgba(245,245,244,0.7)',
              padding: '0 3px',
            }}
          >
            {openTopics.length}
          </span>
        </div>
      ) : (
        <span
          className="flex-shrink-0 ml-1"
          style={{
            fontSize: 10,
            fontWeight: 500,
            color: '#d6d3d1',
            letterSpacing: '0.03em',
          }}
        >
          Topics
        </span>
      )}

      {/* ── Topic tabs (scrollable) ────────────────────────────────────── */}
      <div
        ref={tabsRef}
        className="flex items-center gap-[3px] flex-1 min-w-0 overflow-x-auto"
        style={{ scrollbarWidth: 'none' }}
      >
        {openTopics.map((topic, i) => (
          <TopicTab
            key={topic.id}
            topic={topic}
            isActive={topic.id === focusedTopicId}
            isNewlyCreated={topic.id === newlyCreatedId}
            isNew={initialTabIdsRef.current != null && !initialTabIdsRef.current.has(topic.id)}
            onFocus={() => focusTopic(topic.id === focusedTopicId && !isMinimized ? null : topic.id)}
            onClose={() => removeTopic(topic.id)}
            index={i}
          />
        ))}
      </div>

      {/* ── Right controls ───────────────────────────────────────────── */}
      <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
        {/* Search pill */}
        <button
          className="
            flex items-center gap-1.5 px-2 py-1 rounded-md
            text-neutral-400
            hover:bg-neutral-100/70 hover:text-neutral-600
            transition-all duration-150 flex-shrink-0
            active:scale-[0.97]
          "
          onClick={() => {
            document.dispatchEvent(new KeyboardEvent('keydown', {
              key: 'k',
              metaKey: true,
              bubbles: true,
            }));
          }}
          title="Search (⌘K)"
        >
          <Search size={12} />
          <span className="hidden sm:inline text-[11px]">Search</span>
          <kbd
            className="hidden sm:inline text-[9px] font-mono rounded px-1 py-px"
            style={{
              color: 'rgba(168,162,158,0.8)',
              background: 'rgba(245,245,244,0.8)',
              border: '1px solid rgba(231,229,228,0.6)',
            }}
          >
            ⌘K
          </kbd>
        </button>

        {/* Martha orb — hidden when topic focused (topic has its own input) */}
        {!focusedTopicId && (
          <>
            <div className="w-px flex-shrink-0" style={{ height: 16, background: 'rgba(231,229,228,0.4)' }} />
            <button
              onClick={toggleChat}
              className="relative flex-shrink-0 group"
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgb(var(--c-coral-400, 230 145 128)) 0%, rgb(var(--c-coral-500, 217 127 111)) 50%, rgb(var(--c-coral-600, 198 102 86)) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 4px rgb(var(--c-coral-500, 217 127 111) / 0.3)',
                transition: 'box-shadow 300ms, transform 200ms',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgb(var(--c-coral-500, 217 127 111) / 0.4)';
                (e.currentTarget as HTMLElement).style.transform = 'scale(1.06)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 4px rgb(var(--c-coral-500, 217 127 111) / 0.3)';
                (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
              }}
              title="Martha Chat"
            >
              <Sparkles size={12} className="text-white" style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))' }} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── TopicTab ───────────────────────────────────────────────────────────────────

function TopicTab({
  topic,
  isActive,
  isNewlyCreated,
  isNew,
  onFocus,
  onClose,
  index,
}: {
  topic: Topic;
  isActive: boolean;
  isNewlyCreated?: boolean;
  /** Only true for tabs added after the initial render — controls scaleIn animation */
  isNew?: boolean;
  onFocus: () => void;
  onClose: () => void;
  index: number;
}) {
  const { updateTopic, clearNewlyCreated } = useTopicBar();
  const colorValue = getColorHex(topic.color_index);
  const colorTint = getColorTint(topic.color_index);

  // Inline rename state
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(topic.title);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-enter edit mode for newly created topics
  useEffect(() => {
    if (isNewlyCreated) {
      setEditValue('');
      setIsEditing(true);
      clearNewlyCreated();
    }
  }, [isNewlyCreated, clearNewlyCreated]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const commitRename = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== topic.title) {
      updateTopic(topic.id, { title: trimmed });
      topicsApi.update(topic.id, { title: trimmed }).catch(console.error);
    } else {
      setEditValue(topic.title);
    }
    setIsEditing(false);
  }, [editValue, topic.id, topic.title, updateTopic]);

  const cancelRename = useCallback(() => {
    setEditValue(topic.title);
    setIsEditing(false);
  }, [topic.title]);

  const handleTitleKeyDown = useCallback((e: ReactKeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
    if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
  }, [commitRename, cancelRename]);

  return (
    <div
      className="group relative flex items-center cursor-pointer select-none"
      onClick={() => { if (!isEditing) onFocus(); }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        setEditValue(topic.title);
        setIsEditing(true);
      }}
      style={{
        height: 28,
        paddingLeft: 10,
        paddingRight: 6,
        gap: 6,
        borderRadius: 7,
        maxWidth: 200,
        minWidth: 72,
        background: isActive ? 'white' : 'transparent',
        boxShadow: isActive
          ? '0 1px 3px rgb(var(--c-shadow-color, 47 36 27) / 0.08), 0 0 0 1px rgb(var(--c-neutral-200, 231 229 228) / 0.7)'
          : 'none',
        transition: 'all 220ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        ...(isNew ? {
          animation: 'scaleIn 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          animationDelay: `${index * 30}ms`,
          animationFillMode: 'backwards',
        } : {}),
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          (e.currentTarget as HTMLElement).style.background = colorTint;
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          (e.currentTarget as HTMLElement).style.background = 'transparent';
        }
      }}
    >
      {/* Color accent bar (active) or dot (inactive) */}
      {isActive ? (
        <div
          className="absolute left-0 top-[5px] bottom-[5px] rounded-full"
          style={{
            width: 3,
            backgroundColor: colorValue,
            boxShadow: `0 0 8px ${colorValue}40`,
          }}
        />
      ) : (
        <div
          className="flex-shrink-0 rounded-full"
          style={{
            width: 6,
            height: 6,
            backgroundColor: colorValue,
            opacity: 0.65,
            boxShadow: `0 0 4px ${colorValue}20`,
          }}
        />
      )}

      {/* Title — inline editable on double-click */}
      {isEditing ? (
        <input
          ref={inputRef}
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={commitRename}
          onKeyDown={handleTitleKeyDown}
          onClick={e => e.stopPropagation()}
          placeholder="Name this topic..."
          className="flex-1 min-w-0 bg-transparent focus:outline-none"
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#292524',
            lineHeight: 1.2,
            padding: 0,
            margin: 0,
            border: 'none',
            borderBottom: `1px solid ${colorValue}60`,
          }}
        />
      ) : (
        <span
          className="truncate flex-1 min-w-0"
          style={{
            fontSize: 11,
            fontWeight: isActive ? 600 : 500,
            color: isActive ? '#292524' : '#78716c',
            lineHeight: 1.2,
          }}
          title="Double-click to rename"
        >
          {topic.title}
        </span>
      )}

      {/* Status indicator */}
      <TopicStatusIndicator topic={topic} />

      {/* Background badge */}
      {topic.is_background && !isActive && (
        <span
          className="flex-shrink-0 uppercase"
          style={{
            fontSize: 8,
            fontWeight: 700,
            letterSpacing: '0.06em',
            padding: '1px 4px',
            borderRadius: 3,
            background: 'rgba(245,245,244,0.9)',
            color: '#a8a29e',
          }}
        >
          bg
        </span>
      )}

      {/* Close button — visible on tab hover via group-hover */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="
          flex items-center justify-center flex-shrink-0 rounded
          opacity-0 group-hover:opacity-50 hover:!opacity-100
          transition-all duration-150
        "
        style={{
          width: 16,
          height: 16,
          color: '#d6d3d1',
          ...(isActive ? { opacity: 0.5 } : {}),
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.color = '#57534e';
          el.style.background = 'rgba(231,229,228,0.5)';
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.color = '#d6d3d1';
          el.style.background = 'transparent';
        }}
      >
        <X size={9} />
      </button>

      {/* Active accent line at bottom */}
      {isActive && (
        <div
          className="absolute left-3 right-3 rounded-full"
          style={{
            bottom: -5,
            height: 2,
            backgroundColor: colorValue,
            opacity: 0.7,
          }}
        />
      )}
    </div>
  );
}

// ── Status Indicator ───────────────────────────────────────────────────────────

function TopicStatusIndicator({ topic }: { topic: Topic }) {
  if (topic.needs_attention) {
    return (
      <span className="relative flex-shrink-0" style={{ width: 10, height: 10 }}>
        <span
          className="absolute inset-0 rounded-full animate-ping"
          style={{ backgroundColor: '#E8A93A', opacity: 0.4 }}
        />
        <AlertCircle size={10} className="relative" style={{ color: '#E8A93A' }} />
      </span>
    );
  }

  if (topic.status === 'completed') {
    return <Check size={10} className="flex-shrink-0" style={{ color: '#52A560' }} />;
  }

  return null;
}
